// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {VoiceSceneAnchor} from '@app/features/voice_scene/components/VoiceSceneAnchor';
import styles from '@app/features/voice_scene/components/VoiceSceneStageEffects.module.css';
import type {RoomStageEffects} from '@app/features/voice_scene/hooks/useRoomStageEffects';
import type {Vec3} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const CONFETTI_PIECES = 44;

// Fixed scatter (a small LCG), so a burst costs no random numbers and no layout: each piece only moves and fades.
const PIECES = (() => {
	let seed = 7;
	const next = () => {
		seed = (seed * 1103515245 + 12345) % 2147483648;
		return seed / 2147483648;
	};
	return Array.from({length: CONFETTI_PIECES}, (_, index) => ({
		'--x': `${(next() * 100).toFixed(1)}%`,
		'--dx': `${((next() - 0.5) * 18).toFixed(1)}cqw`,
		'--turn': `${Math.round((next() - 0.5) * 1440)}deg`,
		'--delay': `${Math.round(next() * 450)}ms`,
		'--fall': `${Math.round(2300 + next() * 500)}ms`,
		color: index % 5,
		round: index % 3 === 0,
	}));
})();

// The stage's moments over the room: confetti across the view (not under reduced motion) and a spotlight on someone in
// the room (pointOf: where they are drawn, null when they are not here). Both are one-off CSS animations that
// useRoomStageEffects ends with its one timer.
export const VoiceSceneStageEffects = observer(function VoiceSceneStageEffects({
	effects,
	accent,
	pointOf,
}: {
	effects: RoomStageEffects;
	accent: number | null;
	pointOf: (userId: string) => Vec3 | null;
}) {
	const spotPoint = effects.spot ? pointOf(effects.spot.userId) : null;
	const style =
		accent === null
			? undefined
			: ({'--stage-accent': `#${accent.toString(16).padStart(6, '0')}`} as React.CSSProperties);
	return (
		<>
			{effects.confetti !== null && !Accessibility.useReducedMotion ? (
				<div
					key={effects.confetti}
					className={styles.confetti}
					style={style}
					aria-hidden="true"
					data-stage-confetti=""
					data-flx="voice-scene.voice-scene-stage-effects.confetti"
				>
					{PIECES.map(({color, round, ...vars}, index) => (
						<span
							key={index}
							className={styles.piece}
							data-color={color}
							data-round={round ? '' : undefined}
							style={vars as React.CSSProperties}
							data-flx="voice-scene.voice-scene-stage-effects.piece"
						/>
					))}
				</div>
			) : null}
			{effects.spot && spotPoint ? (
				<VoiceSceneAnchor
					key={effects.spot.key}
					point={spotPoint}
					data-flx="voice-scene.voice-scene-stage-effects.voice-scene-anchor"
				>
					<div
						className={styles.spot}
						style={style}
						data-still={Accessibility.useReducedMotion ? '' : undefined}
						aria-hidden="true"
						data-stage-spot={effects.spot.userId}
						data-flx="voice-scene.voice-scene-stage-effects.spot"
					>
						<span className={styles.beam} data-flx="voice-scene.voice-scene-stage-effects.beam" />
						<span className={styles.ring} data-flx="voice-scene.voice-scene-stage-effects.ring" />
					</div>
				</VoiceSceneAnchor>
			) : null}
		</>
	);
});
